import React, { useEffect } from "react";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  Switch,
  InputNumber,
  Image,
  Tag,
  message,
  Popconfirm,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadImage from "../../components/UploadImage";

interface TestimonialItem {
  _id?: string;
  name: string;
  place: string;
  image: string;
  desc: string;
  status: boolean;
  isDeleted: boolean;
}

interface TestimonialProps {
  testimonialList?: TestimonialItem[];
  form: any;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
  handleSave: () => void;
  handleCancel: () => void;
  onDelete: (id: string) => void;
}

const TestimonialListing: React.FC<TestimonialProps> = ({
  testimonialList = [],
  form,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
  handleSave,
  handleCancel,
  onDelete,
}) => {
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (mode === "edit" && editingIndex !== null) {
      const item = testimonialList[editingIndex];
      if (item) {
        form.setFieldsValue(item);
      }
    } else if (mode === "add") {
      form.resetFields();
    }
  }, [mode, editingIndex, testimonialList, form]);

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setMode("edit");
  };

  const startAdd = () => {
    setEditingIndex(null);
    setMode("add");
    form.resetFields();
  };

  return (
    <div>
      {contextHolder}

      {(mode === "add" || mode === "edit") && (
        <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-2xl shadow-sm mb-6">
          <Form layout="vertical" form={form} className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Author Name"
                name="name"
                rules={[{ required: true, message: "Name is required" }]}
              >
                <Input placeholder="Enter name..." />
              </Form.Item>

              <Form.Item
                label="Place"
                name="place"
                rules={[{ required: true, message: "Place is required" }]}
              >
                <Input placeholder="Enter place..." />
              </Form.Item>
            </div>

            <Form.Item label="Status" name="status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item
              label="Image"
              name="image"
              rules={[{ required: true, message: "Image is required" }]}
            >
              <UploadImage listType="picture-card" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="desc"
              rules={[{ required: true, message: "Description is required" }]}
            >
              <TextArea rows={3} />
            </Form.Item>

            <div className="flex justify-start gap-3 mt-6">
              <ButtonWrapper type="primary" onClick={handleSave} loading={loading}>
                Save
              </ButtonWrapper>
              <ButtonWrapper
                onClick={() => {
                  handleCancel();
                  setMode("list");
                  setEditingIndex(null);
                }}
              >
                Cancel
              </ButtonWrapper>
            </div>
          </Form>
        </div>
      )}

      {mode === "list" && (
        <div className="rounded-[20px] overflow-hidden">
          <table className="min-w-full hidden sm:table">
            <thead>
              <tr className="bg-primary text-white">
                <th className="py-3 px-4 text-left text-sm md:text-base">Image</th>
                <th className="py-3 px-4 text-left text-sm md:text-base">Name</th>
                <th className="py-3 px-4 text-left text-sm md:text-base">Place</th>
                <th className="py-3 px-4 text-left text-sm md:text-base">Description</th>
                <th className="py-3 px-4 text-left text-sm md:text-base">Status</th>
                <th className="py-3 px-4 text-right text-sm md:text-base">Actions</th>
              </tr>
            </thead>
            <tbody>
              {testimonialList.length > 0 ? (
                testimonialList.map((item, index) => (
                  <tr key={item._id || index} className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td className="py-3 px-4">
                      <Image
                        src={item.image}
                        width={60}
                        height={60}
                        className="rounded-md object-cover"
                        fallback="/placeholder.jpg"
                      />
                    </td>
                    <td className="py-3 px-4">{item.name}</td>
                    <td className="py-3 px-4">{item.place}</td>
                    <td className="py-3 px-4">{item.desc}</td>
                    <td className="py-3 px-4">
                      <Tag color={item.status ? "green" : "red"}>
                        {item.status ? "Active" : "Inactive"}
                      </Tag>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-4 text-lg">
                        <EditOutlined
                          className="cursor-pointer text-primary"
                          onClick={() => startEdit(index)}
                        />
                        <Popconfirm
                          title="Are you sure to delete this testimonial?"
                          onConfirm={() => onDelete(item._id!)}
                        >
                          <DeleteOutlined className="cursor-pointer text-red-500" />
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="bg-[#f9f9f9] px-4 py-6 text-center text-gray-500">
                    No testimonials added
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="flex justify-end px-4 py-2 sm:bg-[#f9f9f9]">
            <Button icon={<PlusOutlined />} type="dashed" onClick={startAdd} 
              className="text-primary font-semibold"
            >
              Add Testimonial
            </Button>
          </div>

          <div className="sm:hidden space-y-4 mt-4">
            {testimonialList.map((item, index) => (
              <div
                key={item._id || index}
                className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <Image
                    src={item.image}
                    width={50}
                    height={50}
                    className="rounded-md object-cover"
                    fallback="/placeholder.jpg"
                  />
                  <div>
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-gray-500 text-sm">{item.place}</div>
                  </div>
                </div>
                <p className="mt-2">{item.desc}</p>
                <Tag color={item.status ? "green" : "red"}>
                  {item.status ? "Active" : "Inactive"}
                </Tag>
                <div className="flex justify-end text-primary mt-2 gap-3">
                  <EditOutlined
                    className="cursor-pointer text-lg"
                    onClick={() => startEdit(index)}
                  />
                  <Popconfirm
                    title="Are you sure to delete this testimonial?"
                    onConfirm={() => onDelete(item._id!)}
                  >
                    <DeleteOutlined className="cursor-pointer text-red-500" />
                  </Popconfirm>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialListing;
